import { tt } from '@agos/shared'

/**
 * The three tracking ids the customer site loads and no screen could set - FB-643, §9.359.
 *
 * **A lost feature, not a gap.** `googleAnalyticsId`, `metaPixelId` and `tiktokPixelId` live in
 * `restaurant_settings.basic_settings`, and `customer-consent/src/trackers.ts` loads a tracker for
 * each one that is filled in. So the franchise's analytics and its two ad pixels are whatever the
 * database happens to say - and until this screen the only way to change them was SQL. Legacy had a
 * form (`artifacts/restaurant-manager/src/pages/analytics.tsx:699-833`); AGOS's analytics page was
 * ported as reporting only and left them behind.
 *
 * **Why the logic is here rather than in the component.** The same reason as everywhere else in this
 * module: `trackingFormOf` and `trackingPayload` are the two places a mistake would be invisible on
 * screen - a non-string coming back from an untyped jsonb section, or a save that drops a key and so
 * blanks a live pixel - and both are testable without a DOM.
 *
 * **No validation, deliberately.** §9.359: a GA id is `G-` plus a suffix, a Meta pixel is digits, a
 * TikTok id is alphanumeric, and legacy checks none of them. A wrong id loads a tracker that reports
 * nowhere, which breaks nothing; a regex that rejects a format a vendor introduces next year breaks
 * the screen. The placeholders carry the shape instead.
 */

/** The keys as `basic_settings` spells them. One list, so a form and a payload cannot disagree. */
export const TRACKING_KEYS = ['googleAnalyticsId', 'metaPixelId', 'tiktokPixelId'] as const

export type TrackingKey = (typeof TRACKING_KEYS)[number]

export type TrackingForm = Record<TrackingKey, string>

export const EMPTY_TRACKING: TrackingForm = {
  googleAnalyticsId: '',
  metaPixelId: '',
  tiktokPixelId: '',
}

/**
 * Which cookie decision each tracker waits for - `customer-consent/src/consent.ts:109-111`.
 *
 * Not one category: GA goes in on `analytics`, both pixels on `marketing`. Legacy's helper text says
 * only *"iba ak zákazník súhlasil v cookie banneri"*, which is true and not enough to explain why a
 * filled-in pixel can still report nothing.
 */
export type ConsentCategory = 'analytics' | 'marketing'

export interface TrackerField {
  key: TrackingKey
  /** A product name, so it is not translated. */
  label: string
  placeholder: string
  consent: ConsentCategory
  /** What happens when it is filled in, in the owner's words. */
  help: string
  /** Where the id is found, when the vendor documents it. */
  doc?: string
}

export const TRACKER_FIELDS: readonly TrackerField[] = [
  {
    key: 'googleAnalyticsId',
    label: 'Google Analytics 4 ID',
    placeholder: 'G-XXXXXXXXXX',
    consent: 'analytics',
    help: tt('Vloží sa pri súhlase s analytickými cookies. IP adresu anonymizuje automaticky.'),
    doc: 'https://support.google.com/analytics/answer/9539598',
  },
  {
    key: 'metaPixelId',
    label: 'Meta (Facebook) Pixel ID',
    placeholder: '123456789012345',
    consent: 'marketing',
    help: tt('Vloží sa pri súhlase s marketingovými cookies.'),
    doc: 'https://www.facebook.com/business/help/952192354843755',
  },
  {
    key: 'tiktokPixelId',
    label: 'TikTok Pixel ID',
    placeholder: 'C8XXXXXXXXXXXXXXXXXX',
    consent: 'marketing',
    help: tt('Vloží sa pri súhlase s marketingovými cookies.'),
  },
]

export function consentLabel(category: ConsentCategory): string {
  return category === 'analytics' ? tt('analytické cookies') : tt('marketingové cookies')
}

/**
 * The three ids out of `GET /settings/basic`, and nothing else.
 *
 * The endpoint answers a whole jsonb section - thirteen other keys on this data - so the three are
 * taken **by name**, exactly as `SettingsGeneralPage` takes its four. FB-132's reason was a live
 * Anthropic key sharing the section; the endpoint no longer sends it, and taking what is wanted by
 * name is right regardless of what it includes today.
 *
 * A value that is not a string reads as blank rather than as `[object Object]` in an input: the
 * section is untyped jsonb, so nothing guarantees the shape, and a blank field a save would then
 * clear is the honest rendering of *"this is not an id"*.
 */
export function trackingFormOf(basic: Record<string, unknown> | undefined): TrackingForm {
  const text = (key: TrackingKey): string => {
    const value = basic?.[key]
    return typeof value === 'string' ? value : ''
  }
  return {
    googleAnalyticsId: text('googleAnalyticsId'),
    metaPixelId: text('metaPixelId'),
    tiktokPixelId: text('tiktokPixelId'),
  }
}

/**
 * What gets sent to `PUT /settings/basic`.
 *
 * **Three keys, never the section.** The endpoint is a `mergeSection`, so three keys leave the other
 * thirteen alone. Echoing the section back would mean holding it in form state, which is the thing
 * FB-132 says not to do.
 *
 * **Blank clears, and that is the point.** For a credential the rule is the opposite - an empty field
 * means *"unchanged"*, because a save must not be able to wipe a key somebody cannot re-enter. Here
 * blank genuinely means *no tracker*: it is how measurement gets switched off, and there is nothing
 * to recover because the id is public in the first place.
 */
export function trackingPayload(form: TrackingForm): TrackingForm {
  return {
    googleAnalyticsId: form.googleAnalyticsId.trim(),
    metaPixelId: form.metaPixelId.trim(),
    tiktokPixelId: form.tiktokPixelId.trim(),
  }
}

/** Whether a save would store anything different from what was loaded. */
export function trackingChanged(saved: TrackingForm, next: TrackingForm): boolean {
  const before = trackingPayload(saved)
  const after = trackingPayload(next)
  return TRACKING_KEYS.some((key) => before[key] !== after[key])
}

/**
 * Which ids are filled in - the trackers the customer site will load, given consent.
 *
 * Deliberately *"set"* and not *"active"*: the visitor's cookie decision is the other half, and this
 * screen cannot see it. Saying active would be a claim about 7 000 browsers.
 */
export function setTrackers(form: TrackingForm): TrackingKey[] {
  const payload = trackingPayload(form)
  return TRACKING_KEYS.filter((key) => payload[key] !== '')
}

/**
 * Which trackers a save would switch off.
 *
 * Blanking a field is how it is meant to be done, so this does not refuse or confirm - it says so
 * above the button. A measurement id that vanishes takes the franchise's analytics with it, and
 * nothing else on the screen would show that afterwards: an empty field looks the same whether it was
 * always empty or emptied a second ago.
 */
export function clearedTrackers(saved: TrackingForm, next: TrackingForm): TrackingKey[] {
  const before = trackingPayload(saved)
  const after = trackingPayload(next)
  return TRACKING_KEYS.filter((key) => before[key] !== '' && after[key] === '')
}

export function trackerLabel(key: TrackingKey): string {
  return TRACKER_FIELDS.find((field) => field.key === key)?.label ?? key
}
